export interface AnnotationOutputHashInput {
    output: unknown;
    externalRef?: {
        protocol?: unknown;
        locator?: unknown;
    };
}

const DEEP_HASH_MAX_ARRAY_LENGTH = 200;
const DEEP_HASH_MAX_OBJECT_KEYS = 80;
const DEEP_HASH_MAX_NODE_BUDGET = 1_500;

export function hashAnnotationValue(value: unknown): string {
    return hashString(stableStringify(value));
}

export function buildAnnotationOutputHash({
    output,
    externalRef,
}: AnnotationOutputHashInput): string | undefined {
    if (output === undefined) return undefined;
    const identity = collectOutputIdentity(output, externalRef);
    if (identity.strong) {
        return hashString(`identity:${identity.parts.join("|")}`);
    }
    if (canDeepHash(output)) {
        return hashAnnotationValue(output);
    }
    return hashString(`summary:${identity.parts.join("|")}|${stableStringify(summarizeValue(output, 0))}`);
}

export function hashString(value: string): string {
    let hash = 0x811c9dc5;
    for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

function stableStringify(value: unknown): string {
    if (value == null || typeof value !== "object") {
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return `[${value.map((item) => stableStringify(item)).join(",")}]`;
    }
    const object = value as Record<string, unknown>;
    const keys = Object.keys(object).sort();
    return `{${keys
        .map((key) => `${JSON.stringify(key)}:${stableStringify(object[key])}`)
        .join(",")}}`;
}

function collectOutputIdentity(
    output: unknown,
    externalRef: AnnotationOutputHashInput["externalRef"],
): { parts: string[]; strong: boolean } {
    const parts: string[] = [];
    let strong = false;

    if (externalRef) {
        pushPart(parts, "external.protocol", externalRef.protocol);
        pushPart(parts, "external.locator", externalRef.locator);
    }

    const record = asRecord(output);
    if (!record) {
        pushPart(parts, "value", output);
        return { parts, strong: true };
    }

    const metadata = asRecord(readOwnValue(record, "__metadata"));
    if (metadata) {
        for (const key of [
            "unitType",
            "computedAt",
            "inputHash",
            "pluginVersion",
            "actionName",
            "duration",
            "total",
            "succeeded",
            "failed",
            "source",
            "namespace",
            "symbolCount",
        ]) {
            pushPart(parts, `metadata.${key}`, readOwnValue(metadata, key));
        }
        strong = hasPrimitive(readOwnValue(metadata, "computedAt")) ||
            hasPrimitive(readOwnValue(metadata, "inputHash"));
    }

    for (const key of [
        "executedAt",
        "updatedAt",
        "queryTime",
        "rowCount",
        "dataCount",
        "affectedRows",
        "success",
        "duration",
    ]) {
        const value = readOwnValue(record, key);
        pushPart(parts, key, value);
        if (key === "executedAt" || key === "updatedAt") {
            strong ||= hasPrimitive(value);
        }
    }

    pushArrayLength(parts, "rows", readOwnValue(record, "rows"));
    pushArrayLength(parts, "data", readOwnValue(record, "data"));
    pushArrayLength(parts, "columns", readOwnValue(record, "columns"));

    const result = asRecord(readOwnValue(record, "result"));
    if (result) {
        pushArrayLength(parts, "result.rows", readOwnValue(result, "rows"));
        pushArrayLength(parts, "result.labels", readOwnValue(result, "labels"));
        for (const key of ["stockCount", "dateCount", "columnCount", "rowCount"]) {
            pushPart(parts, `result.${key}`, readOwnValue(result, key));
        }
    }

    if (parts.length === 0) {
        parts.push(`shape:${shapeSummary(record)}`);
    }

    return { parts, strong };
}

function pushPart(parts: string[], key: string, value: unknown): void {
    if (!hasPrimitive(value)) return;
    parts.push(`${key}=${String(value)}`);
}

function pushArrayLength(parts: string[], key: string, value: unknown): void {
    if (Array.isArray(value)) {
        parts.push(`${key}.length=${value.length}`);
    }
}

function hasPrimitive(value: unknown): value is string | number | boolean {
    return (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
    );
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
}

function readOwnValue(record: Record<string, unknown>, key: string): unknown {
    const descriptor = Object.getOwnPropertyDescriptor(record, key);
    if (!descriptor || !("value" in descriptor)) return undefined;
    return descriptor.value;
}

function canDeepHash(value: unknown): boolean {
    const seen = new Set<unknown>();
    let budget = DEEP_HASH_MAX_NODE_BUDGET;

    const visit = (current: unknown): boolean => {
        budget -= 1;
        if (budget < 0) return false;
        if (current == null || typeof current !== "object") return true;
        if (seen.has(current)) return true;
        seen.add(current);

        if (Array.isArray(current)) {
            if (current.length > DEEP_HASH_MAX_ARRAY_LENGTH) return false;
            for (const item of current) {
                if (!visit(item)) return false;
            }
            return true;
        }

        const descriptors = Object.getOwnPropertyDescriptors(current);
        const keys = Object.keys(descriptors);
        if (keys.length > DEEP_HASH_MAX_OBJECT_KEYS) return false;
        for (const key of keys) {
            const descriptor = descriptors[key];
            if (!descriptor || !("value" in descriptor)) return false;
            if (!visit(descriptor.value)) return false;
        }
        return true;
    };

    return visit(value);
}

function summarizeValue(value: unknown, depth: number): unknown {
    if (value == null || typeof value !== "object") return value;
    if (Array.isArray(value)) {
        return {
            type: "array",
            length: value.length,
            sample: value.slice(0, 3).map((item) => summarizeArrayItem(item)),
        };
    }

    const descriptors = Object.getOwnPropertyDescriptors(value);
    const keys = Object.keys(descriptors).sort();
    const summary: Record<string, unknown> = {
        type: "object",
        keys: keys.slice(0, 40),
        keyCount: keys.length,
    };
    if (depth > 0) return summary;

    for (const key of keys.slice(0, 20)) {
        const descriptor = descriptors[key];
        if (!descriptor || !("value" in descriptor)) {
            summary[key] = { type: "accessor" };
            continue;
        }
        summary[key] = summarizeValue(descriptor.value, depth + 1);
    }
    return summary;
}

function summarizeArrayItem(value: unknown): unknown {
    if (value == null || typeof value !== "object") return value;
    if (Array.isArray(value)) {
        return { type: "array", length: value.length };
    }
    return { type: "object", keys: Object.keys(value).sort().slice(0, 12) };
}

function shapeSummary(record: Record<string, unknown>): string {
    return Object.keys(record).sort().slice(0, 40).join(",");
}
